import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import ExportMenu from "@/components/ExportMenu";
import { Building2, Bot, FolderOpen, AlertTriangle, Network, Landmark, MapPin } from "lucide-react";

const REGIME_META = {
  "Hiérarchique": { icon: Network, moteur: "COORDINA", color: "#002664" },
  "Tutelle": { icon: Landmark, moteur: "TUTELA", color: "#b45309" },
  "Déconcentration": { icon: MapPin, moteur: "TERRA", color: "#0f766e" },
};

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <Card className="rounded-sm border-border shadow-none p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{label}</p>
          <p className="font-heading text-3xl font-bold mt-2 text-navy">{value}</p>
        </div>
        <div className="h-9 w-9 flex items-center justify-center rounded-sm" style={{ background: `${accent}14`, color: accent }}>
          <Icon className="h-5 w-5" strokeWidth={1.5} />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)); }, []);

  if (!stats) return <div className="p-8 text-muted-foreground">Chargement…</div>;

  const statusData = Object.entries(stats.dossiers_by_status).map(([k, v]) => ({ name: k, value: v }));
  const STATUS_COLORS = ["#94a3b8", "#2563eb", "#4f46e5", "#d97706", "#7c3aed", "#059669"];

  return (
    <>
      <PageHeader title="Tableau de bord de supervision" subtitle="Vue consolidée — entités, agents, dossiers et régimes de supervision"
        actions={<ExportMenu basePath="/dossiers/export" filename="dossiers_sg_podc" label="Exporter les dossiers" testid="dashboard-export" />} />
      <div className="p-8 space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi icon={Building2} label="Entités supervisées" value={stats.entites_total} accent="#002664" testid="kpi-entites" />
          <Kpi icon={Bot} label="Agents IA" value={stats.agents_total} accent="#0f766e" testid="kpi-agents" />
          <Kpi icon={FolderOpen} label="Dossiers ouverts" value={stats.dossiers_ouverts} accent="#b45309" testid="kpi-dossiers-ouverts" />
          <Kpi icon={AlertTriangle} label="Dossiers (total)" value={stats.dossiers_total} accent="#9d174d" testid="kpi-dossiers-total" />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Régimes */}
          <Card className="rounded-sm border-border shadow-none p-6 lg:col-span-1" data-testid="regimes-card">
            <h3 className="font-heading text-lg font-semibold text-navy">Répartition par régime</h3>
            <p className="text-xs text-muted-foreground mb-4">Chaque régime active son moteur de routage</p>
            <div className="space-y-3">
              {Object.entries(stats.by_regime).map(([regime, count]) => {
                const meta = REGIME_META[regime];
                const Icon = meta.icon;
                return (
                  <div key={regime} className="flex items-center gap-3 border border-border rounded-sm p-3">
                    <div className="h-9 w-9 flex items-center justify-center rounded-sm" style={{ background: `${meta.color}14`, color: meta.color }}>
                      <Icon className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{regime}</p>
                      <p className="text-[11px] text-muted-foreground font-mono">moteur {meta.moteur}</p>
                    </div>
                    <span className="font-heading text-2xl font-bold" style={{ color: meta.color }}>{count}</span>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Dossiers par statut */}
          <Card className="rounded-sm border-border shadow-none p-6 lg:col-span-2" data-testid="status-chart-card">
            <h3 className="font-heading text-lg font-semibold text-navy">Dossiers par statut</h3>
            <p className="text-xs text-muted-foreground mb-4">Cycle de vie : reçu → qualifié → instruction → décision → transmis → clôturé</p>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={statusData} margin={{ top: 8, right: 8, left: -16, bottom: 8 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "#f1f5f9" }} />
                <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                  {statusData.map((entry, i) => <Cell key={entry.name} fill={STATUS_COLORS[i % STATUS_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>

        {/* Charge par agent */}
        <Card className="rounded-sm border-border shadow-none p-6" data-testid="agent-load-card">
          <h3 className="font-heading text-lg font-semibold text-navy">Charge par agent</h3>
          <p className="text-xs text-muted-foreground mb-4">Nombre de sollicitations via le moteur d'orchestration</p>
          {stats.agent_load.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-sm">
              Aucune sollicitation enregistrée pour le moment. Lancez une conversation depuis le Chat PARAFIX.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats.agent_load} layout="vertical" margin={{ left: 24 }}>
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="agent" tick={{ fontSize: 11, fill: "#334155" }} width={90} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "#f1f5f9" }} />
                <Bar dataKey="count" fill="#002664" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
    </>
  );
}
